import React, { useState, useCallback, useEffect } from 'react';
import CustomerContext from './CustomerContext';
import customerModel from '../../model/Customer.model';
import { useLoading } from '../../utils/LoadingUtil';
import { useNavigate } from 'react-router-dom';
import { ConfirmAlert } from '../../components';
import {
  getCustomers,
  createCustomer,
  updateCustomer as updateCustomerService,
  deleteCustomer as deleteCustomerService,
} from '../../services/customerService';
import validateForm from '../../utils/validateForm';
import { handleApiError } from '@/lib/handleApiError';

const CustomerContextProvider = ({ children }) => {
  const { customer, setCustomer, initialState } = customerModel();
  const { isLoading, startLoading, stopLoading } = useLoading();
  const [error, setError] = useState(null);
  const [customerData, setCustomerData] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [sortDir, setSortDir] = useState('desc');

  const nav = useNavigate();

  useEffect(() => {
    setCurrentPage(0);
    setSearchQuery('');
    setPageSize(100);
    setSortBy('');
    setSortDir('desc');
  }, [nav]);

  const isValidData = useCallback(
    (customer) => {
      const errorMessages = {
        customerCode: 'Customer Code is required',
        customerName: 'Customer Name is required',
        email: 'Email is required',
        avgPurchase: 'Average Purchase is required',
      };

      const isValid = validateForm(customer, setError, errorMessages);
      if (!isValid) {
        stopLoading();
        return false;
      }

      if (customer.phone && customer.phone.length < 10) {
        setError({ phone: 'Contact number should be 10 digits' });
        stopLoading();
        return false;
      }

      return true;
    },
    [stopLoading],
  );

  const getAllCustomer = useCallback(
    async (
      page = currentPage,
      size = pageSize,
      search = searchQuery,
      sortField = sortBy, // Renamed parameter to avoid shadowing
      sortDirection = sortDir, // Renamed parameter to avoid shadowing
    ) => {
      startLoading();
      try {
        const response = await getCustomers(
          page,
          size,
          search,
          sortField, // Use renamed parameter
          sortDirection, // Use renamed parameter
        );
        setCustomerData(response?._embedded?.customerDTOList || response);
        setTotalElements(response.page.totalElements);
      } catch (error) {
        setError(error.response?.data || 'An error occurred');
        setTotalElements(0);
      } finally {
        stopLoading();
      }
    },
    [
      currentPage,
      pageSize,
      searchQuery,
      sortBy,
      sortDir,
      startLoading,
      stopLoading,
    ],
  );

  const addCustomer = useCallback(
    async (customer, e, closeModal) => {
      e.preventDefault();
      startLoading();
      if (!isValidData(customer)) return;
      try {
        const formData = new FormData();
        Object.keys(customer).forEach((key) => {
          if (
            key !== 'customerLogoFile' &&
            key !== 'customerLogoUrl' &&
            key !== 'customerLogo'
          ) {
            formData.append(key, customer[key]);
          }
        });

        if (customer.customerLogoFile) {
          formData.append('customerLogoFile', customer.customerLogoFile);
        }

        const response = await createCustomer(formData);
        closeModal();
        getAllCustomer();
        setCustomer(response);
      } catch (error) {
        setError(error.response?.data || 'An error occurred');
      } finally {
        stopLoading();
      }
    },
    [getAllCustomer, isValidData, startLoading, stopLoading],
  );

  const updateCustomer = useCallback(
    async (customer, id, e, closeModal) => {
      e.preventDefault();
      startLoading();
      if (!isValidData(customer)) return;
      try {
        const formData = new FormData();
        Object.keys(customer).forEach((key) => {
          if (
            key !== 'customerLogoFile' &&
            key !== 'customerLogoUrl' &&
            key !== 'customerLogo'
          ) {
            formData.append(key, customer[key]);
          }
        });

        if (customer.customerLogoFile) {
          formData.append('customerLogoFile', customer.customerLogoFile);
        }

        await updateCustomerService(id, formData);
        closeModal();
        clearFormData();
        getAllCustomer();
      } catch (error) {
        setError(error.response?.data || 'An error occurred');
      } finally {
        stopLoading();
      }
    },
    [getAllCustomer, isValidData, startLoading, stopLoading],
  );

  const deleteCustomer = useCallback(
    async (id) => {
      const result = await ConfirmAlert({ confirmBtnText: 'Delete Customer' });
      if (!result.isConfirmed) {
        handleApiError(
          'error',
          'Customer deletion cancelled',
          error?.response?.status,
        );
        return;
      }

      startLoading();
      try {
        await deleteCustomerService(id);
        getAllCustomer();
      } catch (error) {
        setError(error.message || 'An error occurred');
      } finally {
        stopLoading();
      }
    },
    [getAllCustomer, startLoading, stopLoading],
  );

  const clearFormData = useCallback(() => {
    setError(null);
    setCustomer(initialState);
  }, [initialState, setCustomer]);

  const handleSearchQueryChange = (query) => {
    setSearchQuery(query);
    setCurrentPage(0); // Reset to 0th page whenever search query changes
  };

  return (
    <CustomerContext.Provider
      value={{
        getAllCustomer,
        addCustomer,
        updateCustomer,
        deleteCustomer,
        clearFormData,
        customer,
        setCustomer,
        customerData,
        setCustomerData,
        totalElements,
        setTotalElements,
        currentPage,
        setCurrentPage,
        pageSize,
        setPageSize,
        error,
        setError,
        isLoading,
        searchQuery,
        setSearchQuery: handleSearchQueryChange,
        sortBy,
        setSortBy,
        sortDir,
        setSortDir,
      }}>
      {children}
    </CustomerContext.Provider>
  );
};

export default CustomerContextProvider;
export const useCustomer = () => React.useContext(CustomerContext);
